/**
 * Real Estate Investment Analyzer Project Data
 * AI-powered investment analysis platform for real estate properties
 */

import type { Project, Task, Epic, Sprint } from '../types';

const ORG_ID = 'org_intellesphere';
const PROJECT_ID = 'proj-realestate-analyzer';

export const realEstateAnalyzerProject: Project = {
  id: PROJECT_ID,
  organizationId: ORG_ID,
  name: 'Real Estate Investment Analyzer',
  description: 'AI-powered investment analysis platform that scrapes Redfin/Zillow, analyzes properties for remodel vs demo+build profitability using LLM, creates investment profiles, and automates investor outreach',
  status: 'Under Review',
  proposal: `# Real Estate Investment Analyzer - AI-Powered Deal Finder

## Executive Summary
Build an intelligent platform that automatically discovers, analyzes, and presents profitable real estate investment opportunities to potential investors.

## Core Workflow
1. **Property Discovery**: Scrape Redfin, Zillow, Realtor.com for new listings
2. **AI Analysis**: GPT-4 analyzes each property for investment potential
3. **Cost Estimation**: Calculate remodel costs vs demo+rebuild scenarios
4. **ROI Calculation**: Project returns, timelines, and risks
5. **Deal Packaging**: Create professional investment profiles
6. **Investor Matching**: Match deals with investor preferences
7. **Automated Outreach**: Send personalized emails to investors
8. **Deal Tracking**: CRM for managing investor relationships

## Key Features
- **Multi-Source Data**: Redfin, Zillow, Realtor.com, county records
- **LLM-Powered Analysis**: GPT-4 evaluates structural integrity, location value, renovation potential
- **Scenario Modeling**: Compare remodel vs demo+rebuild with detailed breakdowns
- **Market Intelligence**: Neighborhood trends, comps, appreciation forecasts
- **Investor CRM**: Track investor preferences, capital availability, past deals
- **Email Campaigns**: Automated outreach with deal highlights
- **Financial Modeling**: IRR, Cash-on-Cash, Cap Rate calculations
- **Risk Assessment**: Permit risks, contractor availability, market timing

## Technology Stack
- **Web Scraping**: Python (BeautifulSoup, Scrapy, Selenium)
- **AI/ML**: OpenAI GPT-4, LangChain, vector embeddings
- **Backend**: FastAPI (Python) + PostgreSQL
- **Frontend**: Next.js 14 + TypeScript
- **Task Queue**: Celery + Redis
- **Email**: SendGrid with templating
- **Storage**: S3 for property images and documents

## Target Users
- Real estate investors
- House flippers
- Real estate fund managers
- Property developers

## Timeline
10 months to MVP`,
  costs: [
    { id: 'cost-rea-dev', description: 'Development Team (5 engineers × 10 months)', amount: 500000, category: 'Personnel' },
    { id: 'cost-rea-ai', description: 'OpenAI API Credits (GPT-4)', amount: 50000, category: 'Services' },
    { id: 'cost-rea-data', description: 'Real Estate Data APIs', amount: 30000, category: 'Services' },
    { id: 'cost-rea-infra', description: 'AWS Infrastructure', amount: 20000, category: 'Infrastructure' },
  ],
  teamMembers: [
    { userId: 'user-rafi', role: 'developer' },
    { userId: 'user-sakib', role: 'developer' },
    { userId: 'user-tias', role: 'developer' },
    { userId: 'aidimentry', role: 'stakeholder' },
    { userId: 'user-khaled', role: 'stakeholder' },
  ],
  createdAt: '2026-02-23T00:00:00Z',
  updatedAt: '2026-02-23T00:00:00Z',
  externalLinks: [],
  startDate: '2026-05-01',
  targetEndDate: '2027-02-28',
  costPerHour: 100,
};

export const realEstateAnalyzerEpics: Epic[] = [
  {
    id: 'epic-rea-scraping',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Property Data Scraping',
    description: 'Multi-source web scraping from Redfin, Zillow, Realtor.com with anti-detection measures',
    status: 'Not Started',
    taskIds: [],
    standupNotes: [],
    costPerHour: 100,
    createdAt: '2026-02-23T00:00:00Z',
    updatedAt: '2026-02-23T00:00:00Z',
  },
  {
    id: 'epic-rea-ai',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'AI Analysis Engine',
    description: 'GPT-4 powered property analysis, structural assessment, renovation potential evaluation',
    status: 'Not Started',
    taskIds: [],
    standupNotes: [],
    costPerHour: 100,
    createdAt: '2026-02-23T00:00:00Z',
    updatedAt: '2026-02-23T00:00:00Z',
  },
  {
    id: 'epic-rea-cost',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Cost Estimation Models',
    description: 'ML models for renovation cost estimation, demo+build cost calculation, regional adjustments',
    status: 'Not Started',
    taskIds: [],
    standupNotes: [],
    costPerHour: 100,
    createdAt: '2026-02-23T00:00:00Z',
    updatedAt: '2026-02-23T00:00:00Z',
  },
  {
    id: 'epic-rea-profile',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Investment Profile Generator',
    description: 'Automated deal package creation with financials, renders, and pitch materials',
    status: 'Not Started',
    taskIds: [],
    standupNotes: [],
    costPerHour: 100,
    createdAt: '2026-02-23T00:00:00Z',
    updatedAt: '2026-02-23T00:00:00Z',
  },
  {
    id: 'epic-rea-crm',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Investor CRM',
    description: 'Investor database, preference matching, deal history, communication tracking',
    status: 'Not Started',
    taskIds: [],
    standupNotes: [],
    costPerHour: 100,
    createdAt: '2026-02-23T00:00:00Z',
    updatedAt: '2026-02-23T00:00:00Z',
  },
  {
    id: 'epic-rea-financial',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Financial Modeling',
    description: 'ROI calculators, IRR/NPV analysis, sensitivity analysis, risk metrics',
    status: 'Not Started',
    taskIds: [],
    standupNotes: [],
    costPerHour: 100,
    createdAt: '2026-02-23T00:00:00Z',
    updatedAt: '2026-02-23T00:00:00Z',
  },
  {
    id: 'epic-rea-market',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Market Analysis',
    description: 'Neighborhood data, comparable sales, appreciation forecasts, market timing',
    status: 'Not Started',
    taskIds: [],
    standupNotes: [],
    costPerHour: 100,
    createdAt: '2026-02-23T00:00:00Z',
    updatedAt: '2026-02-23T00:00:00Z',
  },
  {
    id: 'epic-rea-automation',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Automation & Workflows',
    description: 'Scheduled scraping, automated analysis, email campaigns, deal tracking',
    status: 'Not Started',
    taskIds: [],
    standupNotes: [],
    costPerHour: 100,
    createdAt: '2026-02-23T00:00:00Z',
    updatedAt: '2026-02-23T00:00:00Z',
  },
];

export const realEstateAnalyzerTasks: Task[] = [
  // Property Data Scraping Epic
  {
    id: 'REA-001',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    epicId: 'epic-rea-scraping',
    title: 'Redfin Web Scraper',
    description: 'Build robust scraper for Redfin with rotating proxies, CAPTCHA solving, and rate limiting',
    type: 'Feature',
    status: 'Not Started',
    priority: 'High',
    complexity: 'High',
    category: 'Backend',
    assigneeIds: [],
    parentTaskIds: [],
    subtaskIds: [],
    estimatedDays: 8,
    estimatedHours: 64,
    timeLogs: [],
    standupNotes: [],
    createdAt: '2026-02-23T00:00:00Z',
    updatedAt: '2026-02-23T00:00:00Z',
    customerVisible: false,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
  },
  {
    id: 'REA-002',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    epicId: 'epic-rea-scraping',
    title: 'Zillow API Integration',
    description: 'Integrate with Zillow API (or scraper fallback) for property listings and details',
    type: 'Feature',
    status: 'Not Started',
    priority: 'High',
    complexity: 'High',
    category: 'Backend',
    assigneeIds: [],
    parentTaskIds: [],
    subtaskIds: [],
    estimatedDays: 7,
    estimatedHours: 56,
    timeLogs: [],
    standupNotes: [],
    createdAt: '2026-02-23T00:00:00Z',
    updatedAt: '2026-02-23T00:00:00Z',
    customerVisible: false,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
  },
  {
    id: 'REA-003',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    epicId: 'epic-rea-scraping',
    title: 'Property Data Normalization',
    description: 'Normalize property data from different sources into unified schema',
    type: 'Feature',
    status: 'Not Started',
    priority: 'High',
    complexity: 'Medium',
    category: 'Backend',
    assigneeIds: [],
    parentTaskIds: ['REA-001', 'REA-002'],
    subtaskIds: [],
    estimatedDays: 5,
    estimatedHours: 40,
    timeLogs: [],
    standupNotes: [],
    createdAt: '2026-02-23T00:00:00Z',
    updatedAt: '2026-02-23T00:00:00Z',
    customerVisible: false,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
  },
  {
    id: 'REA-004',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    epicId: 'epic-rea-scraping',
    title: 'Image Download & Storage',
    description: 'Download property images, optimize, and store in S3 with CDN',
    type: 'Feature',
    status: 'Not Started',
    priority: 'Medium',
    complexity: 'Low',
    category: 'Backend',
    assigneeIds: [],
    parentTaskIds: [],
    subtaskIds: [],
    estimatedDays: 3,
    estimatedHours: 24,
    timeLogs: [],
    standupNotes: [],
    createdAt: '2026-02-23T00:00:00Z',
    updatedAt: '2026-02-23T00:00:00Z',
    customerVisible: false,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
  },

  // AI Analysis Engine Epic
  {
    id: 'REA-010',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    epicId: 'epic-rea-ai',
    title: 'GPT-4 Property Analysis Prompt Engineering',
    description: 'Design and optimize GPT-4 prompts for property structural assessment and investment potential',
    type: 'Feature',
    status: 'Not Started',
    priority: 'Critical',
    complexity: 'High',
    category: 'AI/ML',
    assigneeIds: [],
    parentTaskIds: [],
    subtaskIds: [],
    estimatedDays: 6,
    estimatedHours: 48,
    timeLogs: [],
    standupNotes: [],
    createdAt: '2026-02-23T00:00:00Z',
    updatedAt: '2026-02-23T00:00:00Z',
    customerVisible: false,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
  },
  {
    id: 'REA-011',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    epicId: 'epic-rea-ai',
    title: 'LangChain RAG Implementation',
    description: 'Build RAG system with vector embeddings for property analysis using historical data',
    type: 'Feature',
    status: 'Not Started',
    priority: 'High',
    complexity: 'High',
    category: 'AI/ML',
    assigneeIds: [],
    parentTaskIds: ['REA-010'],
    subtaskIds: [],
    estimatedDays: 8,
    estimatedHours: 64,
    timeLogs: [],
    standupNotes: [],
    createdAt: '2026-02-23T00:00:00Z',
    updatedAt: '2026-02-23T00:00:00Z',
    customerVisible: false,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
  },
  {
    id: 'REA-012',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    epicId: 'epic-rea-ai',
    title: 'Computer Vision for Property Condition',
    description: 'Train CNN model to assess property condition from images (roof, foundation, exterior)',
    type: 'Feature',
    status: 'Not Started',
    priority: 'Medium',
    complexity: 'High',
    category: 'AI/ML',
    assigneeIds: [],
    parentTaskIds: [],
    subtaskIds: [],
    estimatedDays: 15,
    estimatedHours: 120,
    timeLogs: [],
    standupNotes: [],
    createdAt: '2026-02-23T00:00:00Z',
    updatedAt: '2026-02-23T00:00:00Z',
    customerVisible: false,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
  },

  // Cost Estimation Models Epic
  {
    id: 'REA-020',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    epicId: 'epic-rea-cost',
    title: 'Remodel Cost Estimation Model',
    description: 'ML model for estimating renovation costs based on property features, location, and work scope',
    type: 'Feature',
    status: 'Not Started',
    priority: 'Critical',
    complexity: 'High',
    category: 'AI/ML',
    assigneeIds: [],
    parentTaskIds: [],
    subtaskIds: [],
    estimatedDays: 12,
    estimatedHours: 96,
    timeLogs: [],
    standupNotes: [],
    createdAt: '2026-02-23T00:00:00Z',
    updatedAt: '2026-02-23T00:00:00Z',
    customerVisible: false,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
  },
  {
    id: 'REA-021',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    epicId: 'epic-rea-cost',
    title: 'Demo + Rebuild Cost Calculator',
    description: 'Calculate demolition and new construction costs with material pricing and labor rates',
    type: 'Feature',
    status: 'Not Started',
    priority: 'Critical',
    complexity: 'High',
    category: 'Backend',
    assigneeIds: [],
    parentTaskIds: [],
    subtaskIds: [],
    estimatedDays: 10,
    estimatedHours: 80,
    timeLogs: [],
    standupNotes: [],
    createdAt: '2026-02-23T00:00:00Z',
    updatedAt: '2026-02-23T00:00:00Z',
    customerVisible: false,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
  },
  {
    id: 'REA-022',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    epicId: 'epic-rea-cost',
    title: 'Regional Cost Adjustments',
    description: 'Apply regional cost multipliers based on location, labor availability, and permit costs',
    type: 'Feature',
    status: 'Not Started',
    priority: 'High',
    complexity: 'Medium',
    category: 'Backend',
    assigneeIds: [],
    parentTaskIds: ['REA-020', 'REA-021'],
    subtaskIds: [],
    estimatedDays: 5,
    estimatedHours: 40,
    timeLogs: [],
    standupNotes: [],
    createdAt: '2026-02-23T00:00:00Z',
    updatedAt: '2026-02-23T00:00:00Z',
    customerVisible: false,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
  },

  // Investment Profile Generator Epic
  {
    id: 'REA-030',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    epicId: 'epic-rea-profile',
    title: 'Deal Package Generator',
    description: 'Generate professional investment profiles with financials, renders, and executive summary',
    type: 'Feature',
    status: 'Not Started',
    priority: 'High',
    complexity: 'Medium',
    category: 'Backend',
    assigneeIds: [],
    parentTaskIds: [],
    subtaskIds: [],
    estimatedDays: 8,
    estimatedHours: 64,
    timeLogs: [],
    standupNotes: [],
    createdAt: '2026-02-23T00:00:00Z',
    updatedAt: '2026-02-23T00:00:00Z',
    customerVisible: true,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
  },
  {
    id: 'REA-031',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    epicId: 'epic-rea-profile',
    title: 'PDF Export with Financial Tables',
    description: 'Generate PDF investment profiles with property details, photos, and financial projections',
    type: 'Feature',
    status: 'Not Started',
    priority: 'High',
    complexity: 'Medium',
    category: 'Backend',
    assigneeIds: [],
    parentTaskIds: ['REA-030'],
    subtaskIds: [],
    estimatedDays: 5,
    estimatedHours: 40,
    timeLogs: [],
    standupNotes: [],
    createdAt: '2026-02-23T00:00:00Z',
    updatedAt: '2026-02-23T00:00:00Z',
    customerVisible: true,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
  },

  // Investor CRM Epic
  {
    id: 'REA-040',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    epicId: 'epic-rea-crm',
    title: 'Investor Database & Profiles',
    description: 'Build investor database with preferences, investment criteria, and capital availability',
    type: 'Feature',
    status: 'Not Started',
    priority: 'High',
    complexity: 'Medium',
    category: 'Backend',
    assigneeIds: [],
    parentTaskIds: [],
    subtaskIds: [],
    estimatedDays: 6,
    estimatedHours: 48,
    timeLogs: [],
    standupNotes: [],
    createdAt: '2026-02-23T00:00:00Z',
    updatedAt: '2026-02-23T00:00:00Z',
    customerVisible: false,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
  },
  {
    id: 'REA-041',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    epicId: 'epic-rea-crm',
    title: 'Deal Matching Algorithm',
    description: 'Match properties with investor preferences (location, budget, property type, risk tolerance)',
    type: 'Feature',
    status: 'Not Started',
    priority: 'High',
    complexity: 'High',
    category: 'Backend',
    assigneeIds: [],
    parentTaskIds: ['REA-040'],
    subtaskIds: [],
    estimatedDays: 8,
    estimatedHours: 64,
    timeLogs: [],
    standupNotes: [],
    createdAt: '2026-02-23T00:00:00Z',
    updatedAt: '2026-02-23T00:00:00Z',
    customerVisible: false,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
  },
  {
    id: 'REA-042',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    epicId: 'epic-rea-crm',
    title: 'Deal Tracking Pipeline',
    description: 'CRM pipeline for tracking deal progress (sent, viewed, interested, funded)',
    type: 'Feature',
    status: 'Not Started',
    priority: 'Medium',
    complexity: 'Medium',
    category: 'Frontend',
    assigneeIds: [],
    parentTaskIds: ['REA-040'],
    subtaskIds: [],
    estimatedDays: 7,
    estimatedHours: 56,
    timeLogs: [],
    standupNotes: [],
    createdAt: '2026-02-23T00:00:00Z',
    updatedAt: '2026-02-23T00:00:00Z',
    customerVisible: true,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
  },

  // Financial Modeling Epic
  {
    id: 'REA-050',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    epicId: 'epic-rea-financial',
    title: 'ROI & IRR Calculator',
    description: 'Calculate return on investment, internal rate of return, and cash-on-cash returns',
    type: 'Feature',
    status: 'Not Started',
    priority: 'Critical',
    complexity: 'High',
    category: 'Backend',
    assigneeIds: [],
    parentTaskIds: [],
    subtaskIds: [],
    estimatedDays: 8,
    estimatedHours: 64,
    timeLogs: [],
    standupNotes: [],
    createdAt: '2026-02-23T00:00:00Z',
    updatedAt: '2026-02-23T00:00:00Z',
    customerVisible: false,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
  },
  {
    id: 'REA-051',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    epicId: 'epic-rea-financial',
    title: 'Sensitivity Analysis & Risk Modeling',
    description: 'Model different scenarios (best/worst/likely) and calculate risk-adjusted returns',
    type: 'Feature',
    status: 'Not Started',
    priority: 'High',
    complexity: 'High',
    category: 'Backend',
    assigneeIds: [],
    parentTaskIds: ['REA-050'],
    subtaskIds: [],
    estimatedDays: 7,
    estimatedHours: 56,
    timeLogs: [],
    standupNotes: [],
    createdAt: '2026-02-23T00:00:00Z',
    updatedAt: '2026-02-23T00:00:00Z',
    customerVisible: false,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
  },

  // Market Analysis Epic
  {
    id: 'REA-060',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    epicId: 'epic-rea-market',
    title: 'Neighborhood Data Integration',
    description: 'Integrate demographic data, school ratings, crime stats, and walkability scores',
    type: 'Feature',
    status: 'Not Started',
    priority: 'High',
    complexity: 'Medium',
    category: 'Integration',
    assigneeIds: [],
    parentTaskIds: [],
    subtaskIds: [],
    estimatedDays: 6,
    estimatedHours: 48,
    timeLogs: [],
    standupNotes: [],
    createdAt: '2026-02-23T00:00:00Z',
    updatedAt: '2026-02-23T00:00:00Z',
    customerVisible: false,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
  },
  {
    id: 'REA-061',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    epicId: 'epic-rea-market',
    title: 'Comparable Sales Analysis',
    description: 'Find and analyze comparable sales to determine fair market value and ARV',
    type: 'Feature',
    status: 'Not Started',
    priority: 'Critical',
    complexity: 'High',
    category: 'Backend',
    assigneeIds: [],
    parentTaskIds: [],
    subtaskIds: [],
    estimatedDays: 10,
    estimatedHours: 80,
    timeLogs: [],
    standupNotes: [],
    createdAt: '2026-02-23T00:00:00Z',
    updatedAt: '2026-02-23T00:00:00Z',
    customerVisible: false,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
  },
  {
    id: 'REA-062',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    epicId: 'epic-rea-market',
    title: 'Market Trend Forecasting',
    description: 'Predict future appreciation based on historical trends and economic indicators',
    type: 'Feature',
    status: 'Not Started',
    priority: 'Medium',
    complexity: 'High',
    category: 'AI/ML',
    assigneeIds: [],
    parentTaskIds: [],
    subtaskIds: [],
    estimatedDays: 12,
    estimatedHours: 96,
    timeLogs: [],
    standupNotes: [],
    createdAt: '2026-02-23T00:00:00Z',
    updatedAt: '2026-02-23T00:00:00Z',
    customerVisible: false,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
  },

  // Automation & Workflows Epic
  {
    id: 'REA-070',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    epicId: 'epic-rea-automation',
    title: 'Scheduled Property Scraping Jobs',
    description: 'Celery scheduled tasks to scrape new listings daily and update existing properties',
    type: 'Feature',
    status: 'Not Started',
    priority: 'High',
    complexity: 'Medium',
    category: 'Backend',
    assigneeIds: [],
    parentTaskIds: [],
    subtaskIds: [],
    estimatedDays: 4,
    estimatedHours: 32,
    timeLogs: [],
    standupNotes: [],
    createdAt: '2026-02-23T00:00:00Z',
    updatedAt: '2026-02-23T00:00:00Z',
    customerVisible: false,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
  },
  {
    id: 'REA-071',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    epicId: 'epic-rea-automation',
    title: 'Automated Email Campaigns',
    description: 'SendGrid integration with personalized email templates for investor outreach',
    type: 'Feature',
    status: 'Not Started',
    priority: 'High',
    complexity: 'Medium',
    category: 'Backend',
    assigneeIds: [],
    parentTaskIds: [],
    subtaskIds: [],
    estimatedDays: 6,
    estimatedHours: 48,
    timeLogs: [],
    standupNotes: [],
    createdAt: '2026-02-23T00:00:00Z',
    updatedAt: '2026-02-23T00:00:00Z',
    customerVisible: true,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
  },
  {
    id: 'REA-072',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    epicId: 'epic-rea-automation',
    title: 'Deal Scoring & Prioritization',
    description: 'Automated scoring system to prioritize best deals for investor outreach',
    type: 'Feature',
    status: 'Not Started',
    priority: 'High',
    complexity: 'High',
    category: 'AI/ML',
    assigneeIds: [],
    parentTaskIds: [],
    subtaskIds: [],
    estimatedDays: 8,
    estimatedHours: 64,
    timeLogs: [],
    standupNotes: [],
    createdAt: '2026-02-23T00:00:00Z',
    updatedAt: '2026-02-23T00:00:00Z',
    customerVisible: false,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
  },
  {
    id: 'REA-073',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    epicId: 'epic-rea-automation',
    title: 'Email Tracking & Analytics',
    description: 'Track email opens, clicks, and investor responses with analytics dashboard',
    type: 'Feature',
    status: 'Not Started',
    priority: 'Medium',
    complexity: 'Medium',
    category: 'Backend',
    assigneeIds: [],
    parentTaskIds: ['REA-071'],
    subtaskIds: [],
    estimatedDays: 5,
    estimatedHours: 40,
    timeLogs: [],
    standupNotes: [],
    createdAt: '2026-02-23T00:00:00Z',
    updatedAt: '2026-02-23T00:00:00Z',
    customerVisible: true,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
  },
];
